import { h, Component, Fragment } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { QRCodeSVG } from 'qrcode.react';
import md5 from 'crypto-js/md5'
import { route } from 'preact-router';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';

import FormFieldsCompact from '../../components/form/fieldsCompact';
import Input from '../../components/form/input';
import ImageScroller from '../../components/imagescroller';
import InspectionStatus from '../../components/inspection/status';
import GuiList from '../../components/gui/list';
import DateTile from '../../components/gui/dateTile';
import formatRelativeDays from '../../lib/relativeDays';
import '../../components/gui/tileList.css';

function displayImg(img, props, size = '150x') {
    if (typeof img !== 'object') {
        return undefined;
    }
    const imgSrc = `${img.s3MediumLink}`;
    return imgSrc;
}

// Shared look for every status pill on a race row.
const PILL = 'badge rounded-pill me-1';
const PILL_STYLE = 'font-size: 0.7rem; font-weight: 500;';
@withText(props => ({
    titleText: <Text id='race.title'>Races</Text>,
    signupsText: <Text id='race.signups'>signups</Text>,
    titleYourRacesText: <Text id='race.title-your-races'>Your races</Text>,
    helpYourRacesText: <Text id='race.help-your-races'>This is the races you have signed up to.</Text>,
    cancelledText: <Text id='race.cancelled'>Cancelled</Text>,
    signupOpenText: <Text id='race.signup-open'>Signup open</Text>,
}))
@observer
class RaceList extends Component {
  	constructor(props) {
        super(props);
        // initialShowAll is an opt-in prop that seeds the show-all-races
        // state (eg. when arriving via /race?showAll=1 from the past-races
        // strip). Defaults to false so every existing consumer that doesn't
        // pass it renders identically to before.
        const { initialShowAll = false } = props;
        this.state = {
            showallRaces: initialShowAll,
        };
    }

    viewRace = e => {
        const { view, preCallback = () => {} } = this.props;
        preCallback();
        if (view) {
            return view(e);
        }
        const { id, title } = e.target.closest('.box-line').dataset;
        route(`/race/${id}`);
    }

    viewSignup = e => {
        const { drawerLevel = 1 } = this.props;
        const { id } = e.target.closest('.box-line').dataset;
        const raceClassContestantId = parseInt(id, 10);

        const { myRaces } = this.props;
        const raceClassId = myRaces.find(r => r.id === raceClassContestantId).raceClass;

		const { appState } = this.props.stores;
		const { drawerHeightMedium } = appState;
		appState.openDrawerRight('raceSignup', {
            raceClassId,
            isNew: false,
            backButtonText: 'Back',
		}, drawerLevel + 1);
    }

    async loadRace(props = this.props) {
        const { raceStore } = props.stores;
        const { showallRaces } = this.state;
        const query = showallRaces ? { showAll: true } : {};
		await raceStore.load({ addData: ['totalContestants'], query });
	}

    async loadMyRaces(props = this.props) {
		const { raceStore, userStore } = props.stores;
        const { showallRaces } = this.state;
        const query = showallRaces ? { showAll: true } : {};
		await raceStore.loadMyRaces(query);
	}

    toggleShowAllRaces = () => {
        const { showallRaces } = this.state;
        this.setState({ showallRaces: !showallRaces }, () => {
            this.loadRace();
        });
    }

    toggleShowAllYourRaces = () => {
        const { showallYourRaces } = this.state;
        this.setState({ showallYourRaces: !showallYourRaces }, () => {
            this.loadMyRaces();
        });
    }

    gotoTracking = e => {
        const { id, title } = e.target.closest('.box-line').dataset;
        route(`/race/${id}/tracking`);
    }

    gotoResults = e => {
        const { id, title } = e.target.closest('.box-line').dataset;
        route(`/race/${id}/results`);
    }

    renderDateTile = obj => (
        <DateTile date={obj.startDate} isCancelled={!!obj.isCancelled} />
    )

    // «Dine løp» rows are raceClassContestants: they carry the class/race start
    // dates (see get-myraces.js), not their own startDate.
    renderMyRaceDateTile = obj => (
        <DateTile date={obj.raceClassStartDate || obj.raceStartDate} />
    )

    // Replaces the old left qty column («dager igjen», spinner, check, eye-slash).
    // Days-left is hidden on phones; the contestant count, an ongoing race, the
    // signup-open marker and the admin hidden-from-list marker stay visible on
    // every size (the pill-list rule lets the pills wrap on narrow screens).
    // signupOpen comes from the list API (addData=totalContestants) and means
    // at least one raceClass is inside its signup window right now.
    renderStatusBadges = obj => {
        const { signupOpenText } = this.props;
        const now = new Date();
        const isDone = obj.endDate && new Date(obj.endDate) < now;
        const isActive = obj.startDate && obj.endDate && new Date(obj.startDate) < now && new Date(obj.endDate) > now;
        const rel = isActive || isDone ? null : formatRelativeDays(obj.startDate);
        const signupOpen = !!obj.signupOpen && !isDone && !isActive;
        // freeSpots is only set by the API when at least one open class has a
        // musher limit; unlimited races carry no value and get no pill.
        const hasFreeSpots = signupOpen && typeof obj.freeSpots === 'number';
        return (<>
            {isActive ? (
                <span class={`${PILL} text-bg-danger`} style={PILL_STYLE}>
                    <span class='spinner-grow spinner-grow-sm me-1' style='width: 0.5rem; height: 0.5rem;' role='status' />
                    Pågår
                </span>
            ) : ''}
            {isDone ? (
                <span class={`${PILL} text-bg-light`} style={PILL_STYLE} title='Ferdig'>
                    <i class='fa-duotone fa-check text-success' />
                </span>
            ) : ''}
            {rel ? (
                <span class={`${PILL} text-bg-secondary d-none d-sm-inline-block`} style={PILL_STYLE}>{rel}</span>
            ) : ''}
            {signupOpen ? (
                <span class={`${PILL} text-bg-success`} style={PILL_STYLE} title={signupOpenText}>
                    <i class='fa-duotone fa-user-plus me-1' />{signupOpenText}
                </span>
            ) : ''}
            {hasFreeSpots && obj.freeSpots > 0 ? (
                <span class={`${PILL} bg-success-subtle text-success-emphasis border border-success-subtle`} style={PILL_STYLE}>
                    <Text id='race.free-spots' fields={{ count: obj.freeSpots }}>{obj.freeSpots} free spots</Text>
                </span>
            ) : ''}
            {hasFreeSpots && obj.freeSpots === 0 ? (
                <span class={`${PILL} text-bg-danger`} style={PILL_STYLE}>
                    <Text id='race.full'>Full</Text>
                </span>
            ) : ''}
            {obj.totalContestants > 0 && (
                <span class={`${PILL} text-bg-light`} style={PILL_STYLE} title='Påmeldte'>
                    <i class='fa-duotone fa-user me-1' />{obj.totalContestants}
                </span>
            )}
            {obj.hideFromList ? (
                <span class={`${PILL} text-bg-light`} style={PILL_STYLE} title='Skjult fra listen'>
                    <i class='fa-duotone fa-eye-slash text-danger' />
                </span>
            ) : ''}
        </>);
    }

    render() {
        const { appState, userStore, raceStore } = this.props.stores;
        const { isDevelopment, path, apiServer } = appState;
        const { user, isAdmin } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const showSwipeRight2Func = (obj) => !obj.hideResults || isAdmin;
        const swipeRight2ClassFuncComputed = (obj) => obj.hideResults ? 'danger' : 'primary';

        const { showallRaces, showallYourRaces } = this.state;
        const {
            titleText,
            signupsText,
            titleYourRacesText,
            helpYourRacesText,
            cancelledText,
        } = this.props;
        const {
            races,
            classNames = '',
            hideEmpty,
            showEmptyImage = true,
            title = titleText,
            editElement,
            refreshElement,

            swipeRight1Icon = 'fa-duotone fa-map-location',
            swipeRight1Class = 'success',
            swipeRight1Title = 'Tracking',
            swipeRight1Func = this.gotoTracking,

            swipeRight2Icon = 'fa-duotone fa-list',
            swipeRight2Class = 'primary',
            swipeRight2Title = 'Resultater',
            swipeRight2Func = this.gotoResults,

            rightBoxField = '',
            rightBoxFieldValue = e => util.formatDate(e, { month: 'short', day: 'numeric', locale: 'nb-NO' }, true),
            rightBoxFieldPostfix,
            rightBoxFieldPostfixField = 'startDate',
            rightBoxFieldPostfixValue = e => util.formatDate(e, { year: 'numeric', locale: 'nb-NO' }, true),
            rightBoxFieldBottomValue = e => e.totalContestants ? <><i class='fa-duotone fa-user' /> {e.totalContestants}</> : '',
            help,
            children,
            showYourRaces = true,
            myRaces = [],
        } = this.props;

        return (<>
            {showYourRaces && <>
                <GuiList
                    stores={this.props.stores}
                    holdingStore={raceStore}
                    classNames={classNames}
                    helpTextClassNames={'mb-5'}
                    view={this.viewSignup}
                    title={titleYourRacesText}
                    rightButton={<>
                        <button class={`btn btn-sm btn-${showallYourRaces ? '' : 'outline-'}primary rounded-pill`} onClick={this.toggleShowAllYourRaces}>
                            <i class='fa-duotone fa-eye' /> <Text id='race.show-all'>Show all</Text>
                        </button>
                    </>}
                    list={myRaces}
                    elTitle={e => e.raceTitle}
                    // elInfo1={e => e.signupStartDate ? <><i class='fa-duotone fa-user-plus' /> {util.formatDate(e.signupStartDate, { hour12: false, hour: '2-digit', minute: '2-digit' })}</> : ''}
                    elInfo1={e => e.raceClassTitle}
                    elInfo2={e => ''}
                    elInfo3={e => ''}
                    elIconFunc={this.renderMyRaceDateTile}
                    iconStyle='width: 52px; min-width: 52px;'
                    classNamesList={'mx-3 tile-list'}
                    showEmptyImage={showEmptyImage}
                    hideEmpty={hideEmpty}
                    editElement={editElement}
                    rightBoxField={rightBoxField}
                    rightBoxFieldValue={rightBoxFieldValue}
                    rightBoxFieldPostfix={rightBoxFieldPostfix}
                    rightBoxFieldPostfixField={rightBoxFieldPostfixField}
                    rightBoxFieldPostfixValue={rightBoxFieldPostfixValue}
                    help={helpYourRacesText}
                />
            </>}

            <GuiList
                stores={this.props.stores}
                holdingStore={raceStore}
                classNames={classNames}
                view={this.viewRace}
                title={title}
                list={races}
                elTitle={e => `${e.title} ${e.isCancelled ? `(${cancelledText})` : ''}`}
                rightButton={<>
                    <button class={`btn btn-sm btn-${showallRaces ? '' : 'outline-'}primary rounded-pill`} onClick={this.toggleShowAllRaces}>
                        <i class='fa-duotone fa-eye' /> <Text id='race.show-all'>Show all</Text>
                    </button>
                </>}
                // Pills only: the ingress snippet used to follow here, but the
                // .box-line-info text colour is unreadable in dark mode.
                elInfo1={e => this.renderStatusBadges(e)}
                elInfo2={e => ''}
                elIconFunc={this.renderDateTile}
                iconStyle='width: 52px; min-width: 52px;'
                classNamesList={'mx-3 tile-list pill-list'}
                // elInfo3={e => e.totalContestants ? `${e.totalContestants} ${signupsText}` : ''}
                showEmptyImage={showEmptyImage}
                hideEmpty={hideEmpty}
                editElement={editElement}
                refreshElement={refreshElement}
                showFilterBox={showallRaces}

                swipeRight1Icon={swipeRight1Icon}
                swipeRight1Class={swipeRight1Class}
                swipeRight1Title={swipeRight1Title}
                swipeRight1Func={swipeRight1Func}

                swipeRight2Icon={swipeRight2Icon}
                swipeRight2Class={swipeRight2Class}
                swipeRight2ClassFunc={swipeRight2ClassFuncComputed}
                swipeRight2Title={swipeRight2Title}
                swipeRight2Func={swipeRight2Func}
                showSwipeRight2={showSwipeRight2Func}

                rightBoxField={rightBoxField}
                rightBoxFieldValue={rightBoxFieldValue}
                rightBoxFieldPostfix={rightBoxFieldPostfix}
                rightBoxFieldPostfixField={rightBoxFieldPostfixField}
                rightBoxFieldPostfixValue={rightBoxFieldPostfixValue}
                rightBoxFieldBottomValue={rightBoxFieldBottomValue}
                help={help || children}
            />
        </>);
    }
}

export default RaceList;
